/** @format */

import React, { useState, useEffect } from "react";
import useClassroomUsers from "../hooks/useClassroomUsers";
import useCourseUsers from "../hooks/useCourseUsers";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";

const ClassroomUserManager = ({ classroomId, isOpen, onClose }) => {
  const { currentUser, userRole } = useAuth();
  const {
    users: classroomUsers,
    loading,
    operationLoading,
    error,
    addUserToClassroom,
    removeUserFromClassroom,
    bulkAddUsers,
  } = useClassroomUsers(classroomId);

  const { users: availableUsers, loading: usersLoading } = useCourseUsers();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterRole, setFilterRole] = useState("all");
  const [showAuthorsOnly, setShowAuthorsOnly] = useState(false);
  const [classroomData, setClassroomData] = useState(null);
  const [classroomLoading, setClassroomLoading] = useState(true);

  // Debug: Log available users structure
  useEffect(() => {
    if (availableUsers.length > 0) {
      console.log("Available users count:", availableUsers.length);
      console.log("Available users:", availableUsers);
      console.log("First user structure:", availableUsers[0]);
      console.log("First user keys:", Object.keys(availableUsers[0]));
    }
  }, [availableUsers]);

  // Fetch classroom data
  useEffect(() => {
    const fetchClassroomData = async () => {
      if (!classroomId) return;

      try {
        const { doc, getDoc, db } = await import("../firebase/config");
        const classroomRef = doc(db, "classrooms", classroomId);
        const classroomDoc = await getDoc(classroomRef);

        if (classroomDoc.exists()) {
          const data = classroomDoc.data();
          console.log("Classroom data fetched:", data);
          console.log("Classroom profileImageUrl:", data.profileImageUrl);
          setClassroomData(data);
        }
      } catch (error) {
        console.error("Error fetching classroom data:", error);
      } finally {
        setClassroomLoading(false);
      }
    };

    fetchClassroomData();
  }, [classroomId]);

  // Filter available users based on search, role, and authors-only filter
  const filteredAvailableUsers = availableUsers.filter((user) => {
    const matchesSearch =
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase());

    // Handle role filtering for both array and string roles
    const matchesRole =
      filterRole === "all" ||
      (() => {
        if (Array.isArray(user.role)) {
          return user.role.includes(filterRole);
        }
        return user.role === filterRole;
      })();

    // Handle authors-only filter
    const isAuthor = (() => {
      if (Array.isArray(user.role)) {
        return user.role.some(role => 
          role === "author" || role === "instructor" || role === "teacher"
        );
      }
      return user.role === "author" || user.role === "instructor" || user.role === "teacher";
    })();

    const matchesAuthorsFilter = !showAuthorsOnly || isAuthor;

    // Check if user is already in classroom using both uid and id
    const userUid = user.uid || user.id;
    const notInClassroom = !classroomUsers.find((cu) => cu.userId === userUid);

    return matchesSearch && matchesRole && matchesAuthorsFilter && notInClassroom;
  });

  // Debug: Log filtering results
  useEffect(() => {
    if (availableUsers.length > 0) {
      console.log("Filtering results:");
      console.log("- Total users:", availableUsers.length);
      console.log("- Search term:", searchTerm);
      console.log("- Role filter:", filterRole);
      console.log("- Users in classroom:", classroomUsers.length);
      console.log("- Filtered available users:", filteredAvailableUsers.length);
    }
  }, [
    availableUsers,
    searchTerm,
    filterRole,
    classroomUsers,
    filteredAvailableUsers,
  ]);

  // Handle adding a single user
  const handleAddUser = async (user, classroomRole = null) => {
    try {
      // Check if user has uid or id field
      const userId = user.uid || user.id;
      if (!userId) {
        console.error("User object missing uid/id:", user);
        toast.error("Invalid user data");
        return;
      }

      // Determine classroom role based on user's global role
      let assignedClassroomRole = classroomRole;
      if (!assignedClassroomRole) {
        if (Array.isArray(user.role)) {
          if (user.role.includes("admin")) {
            assignedClassroomRole = "admin";
          } else if (user.role.includes("author") || user.role.includes("instructor") || user.role.includes("teacher")) {
            assignedClassroomRole = "author";
          } else {
            assignedClassroomRole = "student";
          }
        } else {
          if (user.role === "admin") {
            assignedClassroomRole = "admin";
          } else if (user.role === "author" || user.role === "instructor" || user.role === "teacher") {
            assignedClassroomRole = "author";
          } else {
            assignedClassroomRole = "student";
          }
        }
      }

      const userData = {
        userId: userId,
        userName: user.name,
        userEmail: user.email,
        userRole: user.role,
        classroomRole: assignedClassroomRole,
        addedBy: currentUser?.uid || 'system',
      };

      console.log("Adding user to classroom:", userData);
      const result = await addUserToClassroom(userData);

      if (result.success) {
        setSearchTerm("");
        const roleText = assignedClassroomRole === "author" ? "as an author" : `as a ${assignedClassroomRole}`;
        toast.success(`User added successfully ${roleText}!`);
      } else {
        toast.error(result.error || "Failed to add user");
      }
    } catch (error) {
      console.error("Error adding user:", error);
      toast.error("Failed to add user to classroom");
    }
  };

  // Handle bulk adding users
  const handleBulkAdd = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select users to add");
      return;
    }

    try {
      const usersData = selectedUsers
        .map((userId) => {
          const user = availableUsers.find(
            (u) => u.uid === userId || u.id === userId
          );
          if (!user) {
            console.error("User not found for ID:", userId);
            return null;
          }

          const userUid = user.uid || user.id;
          
          // Determine classroom role based on user's global role
          let assignedClassroomRole;
          if (Array.isArray(user.role)) {
            if (user.role.includes("admin")) {
              assignedClassroomRole = "admin";
            } else if (user.role.includes("author") || user.role.includes("instructor") || user.role.includes("teacher")) {
              assignedClassroomRole = "author";
            } else {
              assignedClassroomRole = "student";
            }
          } else {
            if (user.role === "admin") {
              assignedClassroomRole = "admin";
            } else if (user.role === "author" || user.role === "instructor" || user.role === "teacher") {
              assignedClassroomRole = "author";
            } else {
              assignedClassroomRole = "student";
            }
          }

          return {
            userId: userUid,
            userName: user.name,
            userEmail: user.email,
            userRole: user.role,
            classroomRole: assignedClassroomRole,
            addedBy: currentUser?.uid || 'system',
          };
        })
        .filter(Boolean); // Remove any null entries

      console.log("Bulk adding users:", usersData);
      const result = await bulkAddUsers(usersData);

      if (result.success) {
        setSelectedUsers([]);
        setSearchTerm("");
        const authorCount = usersData.filter(u => u.classroomRole === "author").length;
        const studentCount = usersData.filter(u => u.classroomRole === "student").length;
        const adminCount = usersData.filter(u => u.classroomRole === "admin").length;
        
        let message = `Successfully added ${result.addedCount} users!`;
        if (authorCount > 0) message += ` (${authorCount} authors)`;
        if (studentCount > 0) message += ` (${studentCount} students)`;
        if (adminCount > 0) message += ` (${adminCount} admins)`;
        
        toast.success(message);
      } else {
        toast.error(result.error || "Failed to add users");
      }
    } catch (error) {
      console.error("Error bulk adding users:", error);
      toast.error("Failed to add users to classroom");
    }
  };

  // Handle removing a user
  const handleRemoveUser = async (userId) => {
    if (
      window.confirm(
        "Are you sure you want to remove this user from the classroom?"
      )
    ) {
      await removeUserFromClassroom(userId);
    }
  };

  // Toggle user selection for bulk operations
  const toggleUserSelection = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // Get role badge styling
  const getRoleBadge = (role) => {
    const roleStyles = {
      admin: "bg-red-100 text-red-800",
      author: "bg-blue-100 text-blue-800",
      student: "bg-green-100 text-green-800",
      default: "bg-gray-100 text-gray-800",
    };
    return roleStyles[role] || roleStyles.default;
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusStyles = {
      active: "bg-green-100 text-green-800",
      inactive: "bg-red-100 text-red-800",
      pending: "bg-yellow-100 text-yellow-800",
      default: "bg-gray-100 text-gray-800",
    };
    return statusStyles[status] || statusStyles.default;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-12 w-12 mr-4">
              {classroomLoading ? (
                <div className="h-12 w-12 rounded-lg bg-gray-200 animate-pulse flex items-center justify-center">
                  <svg
                    className="h-6 w-6 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              ) : classroomData?.profileImageUrl ? (
                <img
                  className="h-12 w-12 rounded-lg object-cover border-2 border-gray-200"
                  src={classroomData.profileImageUrl}
                  alt={`${classroomData?.name || "Classroom"} thumbnail`}
                  onError={(e) => {
                    console.log("Image failed to load, showing fallback");
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : null}
              <div
                className={`h-12 w-12 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center ${
                  !classroomLoading && !classroomData?.profileImageUrl
                    ? "flex"
                    : "hidden"
                }`}
              >
                <svg
                  className="h-6 w-6 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {classroomLoading
                  ? "Loading..."
                  : classroomData?.name || "Manage Classroom Users"}
              </h2>
              <p className="text-sm text-gray-500">
                {classroomLoading ? (
                  "Fetching classroom details..."
                ) : (
                  <>
                    {classroomData?.subject && `${classroomData.subject} • `}
                    Classroom ID: {classroomId}
                  </>
                )}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Classroom Info */}
        {classroomData && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {classroomData.memberCount || 0}
                </div>
                <div className="text-sm text-gray-600">Current Members</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {classroomData.maxMembers || 0}
                </div>
                <div className="text-sm text-gray-600">Max Capacity</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {classroomData.category || "N/A"}
                </div>
                <div className="text-sm text-gray-600">Category</div>
              </div>
            </div>

            {/* Debug Info - Remove this in production */}
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
              <strong>Debug Info:</strong> profileImageUrl:{" "}
              {classroomData.profileImageUrl || "Not set"}
            </div>
          </div>
        )}

        {/* Current Classroom Users */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            Current Users ({classroomUsers.length})
          </h3>

          {loading ? (
            <div className="text-center py-4">Loading current users...</div>
          ) : error ? (
            <div className="text-red-600 py-4">{error}</div>
          ) : classroomUsers.length === 0 ? (
            <div className="text-gray-500 py-4">
              No users in this classroom yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Global Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Classroom Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {classroomUsers.map((classroomUser) => (
                    <tr key={classroomUser.userId}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            {classroomUser.profileImageUrl ? (
                              <img
                                className="h-10 w-10 rounded-full object-cover"
                                src={classroomUser.profileImageUrl}
                                alt={classroomUser.name || "User"}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.nextSibling.style.display = "flex";
                                }}
                              />
                            ) : null}
                            <div
                              className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-medium text-white ${
                                !classroomUser.profileImageUrl
                                  ? "flex"
                                  : "hidden"
                              } ${
                                classroomUser.role === "admin"
                                  ? "bg-red-500"
                                  : classroomUser.role === "author"
                                  ? "bg-blue-500"
                                  : "bg-green-500"
                              }`}
                            >
                              {(
                                classroomUser.name ||
                                classroomUser.email ||
                                "U"
                              )
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {classroomUser.name || "Unknown User"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {classroomUser.email || "No email"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadge(
                            classroomUser.role
                          )}`}
                        >
                          {classroomUser.role || "student"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            classroomUser.classroomRole === "author"
                              ? "bg-purple-100 text-purple-800"
                              : classroomUser.classroomRole === "admin"
                              ? "bg-red-100 text-red-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {classroomUser.classroomRole || "student"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(
                            classroomUser.status
                          )}`}
                        >
                          {classroomUser.status || "active"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleRemoveUser(classroomUser.userId)}
                          disabled={operationLoading}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Author Assignment Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-700 flex items-center">
              <span className="text-2xl mr-2">📚</span>
              Assign Authors to Classroom
            </h3>
            <div className="text-sm text-gray-500">
              Authors can create and manage classroom content
            </div>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <span className="text-2xl">💡</span>
              </div>
              <div className="ml-3">
                <h4 className="text-sm font-medium text-blue-800">
                  Quick Author Assignment
                </h4>
                <p className="text-sm text-blue-700">
                  Use the "Authors Only" filter below to quickly find and assign authors to this classroom. 
                  Authors will have permission to create and manage classroom content.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Add Users Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-700 mb-4">
            Add Users to Classroom
          </h3>

          {/* Search and Filter */}
          <div className="space-y-4 mb-4">
            {/* Search Bar */}
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="author">Author</option>
                <option value="instructor">Instructor</option>
                <option value="teacher">Teacher</option>
                <option value="student">Student</option>
                <option value="user">User</option>
              </select>
            </div>
            
            {/* Quick Filters */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowAuthorsOnly(!showAuthorsOnly)}
                className={`px-3 py-1 text-sm font-medium rounded-full border transition-colors ${
                  showAuthorsOnly
                    ? "bg-blue-100 text-blue-800 border-blue-300"
                    : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                }`}
              >
                📚 Authors Only
              </button>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setFilterRole("all");
                  setShowAuthorsOnly(false);
                }}
                className="px-3 py-1 text-sm font-medium rounded-full border border-gray-300 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* Available Users */}
          {usersLoading ? (
            <div className="text-center py-4">Loading available users...</div>
          ) : (
            <>
              <div className="mb-2 text-sm text-gray-600">
                <div className="flex flex-wrap gap-4">
                  <span>Total users: {availableUsers.length}</span>
                  <span>Available to add: {filteredAvailableUsers.length}</span>
                  <span>Already in classroom: {classroomUsers.length}</span>
                  {showAuthorsOnly && (
                    <span className="text-blue-600 font-medium">
                      📚 Showing authors only
                    </span>
                  )}
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={
                            selectedUsers.length ===
                              filteredAvailableUsers.length &&
                            filteredAvailableUsers.length > 0
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              // Only select users that are not already in the classroom
                              const availableUserIds = filteredAvailableUsers
                                .filter((user) => {
                                  const userUid = user.uid || user.id;
                                  return !classroomUsers.find(
                                    (cu) => cu.userId === userUid
                                  );
                                })
                                .map((u) => u.uid || u.id);
                              setSelectedUsers(availableUserIds);
                            } else {
                              setSelectedUsers([]);
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Global Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Will Be Assigned As
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAvailableUsers.map((user) => (
                      <tr key={user.uid || user.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(
                              user.uid || user.id
                            )}
                            onChange={() =>
                              toggleUserSelection(user.uid || user.id)
                            }
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              {user.profileImageUrl ? (
                                <img
                                  className="h-10 w-10 rounded-full object-cover"
                                  src={user.profileImageUrl}
                                  alt={user.name || "User"}
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                    e.target.nextSibling.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <div
                                className={`h-10 w-10 rounded-full flex items-center justify-center text-sm font-medium text-white ${
                                  !user.profileImageUrl ? "flex" : "hidden"
                                } ${
                                  user.role === "admin"
                                    ? "bg-red-500"
                                    : user.role === "author"
                                    ? "bg-blue-500"
                                    : "bg-green-500"
                                }`}
                              >
                                {(user.name || user.email || "U")
                                  .charAt(0)
                                  .toUpperCase()}
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {user.name || "Unknown User"}
                              </div>
                              <div className="text-sm text-gray-500">
                                {user.email || "No email"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRoleBadge(
                              user.role || "student"
                            )}`}
                          >
                            {user.role || "student"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            let assignedRole;
                            if (Array.isArray(user.role)) {
                              if (user.role.includes("admin")) {
                                assignedRole = "admin";
                              } else if (user.role.includes("author") || user.role.includes("instructor") || user.role.includes("teacher")) {
                                assignedRole = "author";
                              } else {
                                assignedRole = "student";
                              }
                            } else {
                              if (user.role === "admin") {
                                assignedRole = "admin";
                              } else if (user.role === "author" || user.role === "instructor" || user.role === "teacher") {
                                assignedRole = "author";
                              } else {
                                assignedRole = "student";
                              }
                            }
                            
                            return (
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  assignedRole === "author"
                                    ? "bg-purple-100 text-purple-800"
                                    : assignedRole === "admin"
                                    ? "bg-red-100 text-red-800"
                                    : "bg-green-100 text-green-800"
                                }`}
                              >
                                {assignedRole}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(
                              user.status || "active"
                            )}`}
                          >
                            {user.status || "active"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleAddUser(user)}
                              disabled={operationLoading}
                              className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                            >
                              Add
                            </button>
                            {/* Show special "Add as Author" button for users with author roles */}
                            {(() => {
                              const isAuthor = Array.isArray(user.role) 
                                ? user.role.some(role => role === "author" || role === "instructor" || role === "teacher")
                                : user.role === "author" || user.role === "instructor" || user.role === "teacher";
                              
                              return isAuthor ? (
                                <button
                                  onClick={() => handleAddUser(user, "author")}
                                  disabled={operationLoading}
                                  className="text-purple-600 hover:text-purple-900 disabled:opacity-50 font-medium"
                                  title="Add as classroom author with content creation permissions"
                                >
                                  📚 Add as Author
                                </button>
                              ) : null;
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Bulk Actions */}
          {selectedUsers.length > 0 && (
            <div className="mt-4 flex gap-2">
              <button
                onClick={handleBulkAdd}
                disabled={operationLoading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Add {selectedUsers.length} Selected Users
              </button>
              <button
                onClick={() => setSelectedUsers([])}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
              >
                Clear Selection
              </button>
            </div>
          )}

          {filteredAvailableUsers.length === 0 && (
            <div className="text-center py-4 text-gray-500">
              {searchTerm
                ? "No users found matching your search criteria."
                : "No users available to add to this classroom."}
            </div>
          )}
        </div>

        {/* Close Button */}
        <div className="mt-8 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClassroomUserManager;
